import { z } from 'zod';
import { IRowMeta, RowType } from '../../redux/slices/oldBuilder';
import { ZodProperties } from '../../utils/zod';
import { IBasicFilterFieldsFragment } from '../../utils/apollo/graphqlSchema';
import { IEventEntitySelection } from '../Builder/BuilderFields/BuilderEventsInput/types';

// change name to entity drop zone data type
export enum EntityDropzoneDataTypeEnum {
  Events = 'EVENTS',
  Measures = 'MEASURES',
  Cohorts = 'COHORTS',

  Significance = 'SIGNIFICANCE',
  Breakdowns = 'BREAKDOWNS',
  Filters = 'FILTERS',
  Fields = 'FIELDS',

  Property = 'PROPERTY',
}

const breakdownFieldSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Breakdowns),
  groupLimit: z.number().nonnegative().optional(),
  groupIndex: z.number().nonnegative().optional(),
  errorKey: z.string().optional(),
});

const rowMetaSchema = z.object<ZodProperties<IRowMeta>>({
  rowType: z.nativeEnum(RowType),
  rowIndex: z.number(),
  eventIndex: z.number().optional(),
  conditionIndex: z.number().optional(),
  predicateIndex: z.number().optional(),
  numberIndex: z.number().optional(),
  negativeStepRowIndex: z.number().optional(),
  pathDirection: z.string().optional(),
  isLastStep: z.boolean().optional(),
  cohortRowType: z.literal('cohortFilter').optional(),
});

const eventSchema = z
  .object<ZodProperties<Pick<IEventEntitySelection, '__typename'>>>({
    __typename: z.union([
      z.literal('VirtualEvent'),
      z.literal('BasicEventValue'),
      z.literal('AnyTableEvent'),
      z.literal('AnyEvent'),
    ]),
  })
  .passthrough()
  .transform((event) => event as IEventEntitySelection);

const cohortDropzoneSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Cohorts),
  rowMeta: rowMetaSchema,
  errorKey: z.string().optional(),
  addCohortRow: z.boolean().optional(),
});

const measureDropzoneSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Measures),
  rowMeta: rowMetaSchema,
  addCohortRow: z.boolean().optional(),
  errorKey: z.string().optional(),
});

const eventDropzoneSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Events),
  rowMeta: rowMetaSchema,
  isNewEvent: z.boolean().optional(),
  shouldAddMeasureFunction: z.boolean().optional(),
  existingEvents: z.array(eventSchema).optional(),
  isNewRow: z.boolean().optional(),
  addCohortRow: z.boolean().optional(),
  errorKey: z.string().optional(),
});

const basicFilterSchema = z
  .object<ZodProperties<Pick<IBasicFilterFieldsFragment, '__typename'>>>({
    __typename: z.literal('BasicFilter'),
  })
  .passthrough()
  .transform((basicFilter) => basicFilter as IBasicFilterFieldsFragment);

const fieldDropzoneSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Fields),
  rowMeta: rowMetaSchema,
  filter: basicFilterSchema.optional(),
  conditionIndex: z.number().optional(),
  predicateIndex: z.number().optional(),
  errorKey: z.string().optional(),
});

const propertyDropzoneSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Property),
  rowMeta: rowMetaSchema,
  errorKey: z.string().optional(),
});

const namedFilterDropzoneSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Filters),
  rowMeta: rowMetaSchema,
  errorKey: z.string().optional(),
});

const significanceDropzoneSchema = z.object({
  id: z.string().optional(),
  type: z.literal(EntityDropzoneDataTypeEnum.Significance),
  variantIndex: z.number().optional(),
  errorKey: z.string().optional(),
});

const entityDropzoneDataSchema = z.discriminatedUnion('type', [
  breakdownFieldSchema,
  eventDropzoneSchema,
  fieldDropzoneSchema,
  namedFilterDropzoneSchema,
  significanceDropzoneSchema,
  measureDropzoneSchema,
  cohortDropzoneSchema,
  propertyDropzoneSchema,
]);

const droppableContainerSchema = z.object({
  data: z
    .object({
      current: z
        .object({
          data: entityDropzoneDataSchema,
        })
        .nullable(),
    })
    .nullable(),
});

export const EntityDropzoneSchema = z.object({
  data: z
    .object({
      droppableContainer: droppableContainerSchema.nullable(),
    })
    .nullable(),
});

export type IBreakdownDropzoneProperties = z.infer<typeof breakdownFieldSchema>;
export type IEntityDropzoneDataProperties = z.infer<
  typeof entityDropzoneDataSchema
>;

export type IEventEntityDropzoneDataType = z.infer<typeof eventDropzoneSchema>;
export type IMeasureEntityDropzoneDataType = z.infer<
  typeof measureDropzoneSchema
>;
export type ICohortEntityDropzoneDataType = z.infer<
  typeof cohortDropzoneSchema
>;
export type ISignificanceDropzoneDataType = z.infer<
  typeof significanceDropzoneSchema
>;

export type INamedFilterEntityDropzoneDataType = z.infer<
  typeof namedFilterDropzoneSchema
>;
export type IFieldEntityDropzoneDataType = z.infer<typeof fieldDropzoneSchema>;
export type IPropertyEntityDropzoneDataType = z.infer<
  typeof propertyDropzoneSchema
>;
